import { BCCollapse, ICollapsePanelProps, ICollapseToolboxItem } from '@bimser/components';
import ICollapseItem from '@bimser/components/dist/BCCollapse/entities/ICollapseItem';
import { FormItemTypes, IDictionary } from '@bimser/core';
import cloneDeep from 'lodash/cloneDeep';
import { JSX } from 'react';
import { ITabItem } from '../../../../entities/common/ITabItem';
import { IUserEvent } from '../../../../entities/services/IUserEvent';
import { geti18nValue } from '../../../../helper/geti18nValue';
import { IToolboxItemClickEventArgs } from '../../entities/EventArgs/IToolboxItemClickEventArgs';
import Tabs from '../Tabs';
import { ICollapseProperties } from './entities/ICollapseProperties';
import { IToolboxItem } from './entities/IToolboxItem';

export default class Collapse extends Tabs {
	accordion: boolean;
	expandedKeys: string[];
	toolboxItems: IToolboxItem[];

	constructor() {
		super();
		this.controlType = FormItemTypes.EControlTypes.Collapse;
		this.renderTabContent = this.renderTabContent.bind(this);
		this.onChangeExpandedKeys = this.onChangeExpandedKeys.bind(this);
		this.onToolboxItemClick = this.onToolboxItemClick.bind(this);
		this.toolboxItems = [];

		this.subscribe('trigger:OnClick', (value: string) => {
			this.onChangeExpandedKeys(JSON.parse(value));
		});
	}

	__loadProperties(data: ICollapseProperties) {
		super.__loadProperties(data);
		this.__proxyChanged.accordion = data.accordion;
		this.__proxyChanged.toolboxItems = data.toolboxItems;
		this.__proxyChanged.expandedKeys = this.__getExpandedKeys(data.items);
	}

	private setExpandedKeys(items: ITabItem<any>[], expandedKeys: Array<string> | string) {
		items.forEach((item: ITabItem<any>, index: number) => {
			if (expandedKeys && expandedKeys.indexOf(item.key) !== -1) {
				item.selected = true;
			} else {
				item.selected = false;
			}
		});

		return items;
	}

	private onChangeExpandedKeys(expandedKeys: Array<string> | string) {
		const _collapseItems = this.setExpandedKeys(cloneDeep(this.items), expandedKeys);
		this.__proxyChanging.items = _collapseItems;
		this.__proxyChanging.expandedKeys = this.__getExpandedKeys(_collapseItems);
	}

	private __getExpandedKeys(items: ITabItem<any>[]) {
		const expandedKeys: string[] = [];
		if (items?.length) {
			items.forEach((item: ITabItem<any>, index: number) => {
				if (item.selected) {
					expandedKeys.push(item.key);
				}
			});
		}
		return expandedKeys;
	}

	private onToolboxItemClick(name: string) {
		const argument: IToolboxItemClickEventArgs = {
			name: name
		};

		this.emit<IToolboxItemClickEventArgs>('event:OnToolboxItemClick', argument);
		this.emit<IUserEvent<IToolboxItemClickEventArgs>>('event:server:OnToolboxItemClick', {
			argument,
			controlId: this.name,
			name: 'ToolboxItemClick',
			sender: 'web'
		});
	}

	private getItems(): ICollapsePanelProps['items'] {
		if (this.items?.length) {
			return this.items
				.filter(i => i.enabled)
				.map(i => ({
					key: i.key,
					label: i.title.getText(),
					children: this.renderTabContent(i.key)
				}));
		}
		return [
			{
				key: 'collapse1',
				label: 'Collapse1'
			},
			{
				key: 'collapse2',
				label: 'Collapse2'
			}
		];
	}

	private getToolboxItems(): ICollapseToolboxItem[] {
		let data: ICollapseToolboxItem[] = [];
		data = this.toolboxItems.map(i => ({
			key: i.key,
			text: geti18nValue(i.text, this.__form.currentLanguage),
			icon: i.icon,
			farItem: i.farItem,
			active: i.active,
			showText: i.showText
		}));
		return data;
	}

	__getComponentData(): IDictionary<any> {
		return {
			...super.__getComponentData(),
			accordion: this.accordion,
			expandedKeys: this.expandedKeys,
			toolboxItems: this.toolboxItems
		};
	}

	__render(children?: JSX.Element) {
		return super.__render(
			<BCCollapse
				items={this.getItems()}
				hasScrollbar={this.hasScrollbar}
				scrollbarStyles={{
					height: this.style.height + 'px'
				}}
				toolboxItems={this.getToolboxItems()}
				expandedKeys={this.__getExpandedKeys(this.items)}
				onChangeExpandedKeys={this.onChangeExpandedKeys}
				onToolboxItemClick={this.onToolboxItemClick}
				accordion={this.accordion}
			/>
		);
	}
}
